import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError, AxiosResponse } from "axios";

import { apiInstance } from "@/shared/api";
import { useToastNotifications } from "@/shared/hooks/useToastNotifications";
import type { PaymentStatusRequest, RequestPaymentRequest } from "./api.dto";
import { filterObjectKeys } from "@/shared/lib/utils";
import type {
  PaginatedResponse,
  PaginationRequest,
  TableApiCallParams,
} from "@/shared/types";
import { useToast } from "@/shared/hooks";
import type { Payment } from "../model";

export const usePaymentStatus = () => {
  const { onSuccess, onError } = useToastNotifications({
    toast: {
      success: {
        description: "Статус оплаты изменен.",
      },
    },
    refetchQueries: ["payouts"],
  });
  return useMutation<AxiosResponse, AxiosError, PaymentStatusRequest>({
    mutationFn: (data) =>
      apiInstance.patch(`/payments/${data.id}/status/${data.status}`),
    onSuccess,
    onError,
  });
};

export const usePayouts = (props: PaginationRequest) => {
  const params = filterObjectKeys(props);

  return useQuery<PaginatedResponse<Payment>>({
    queryKey: ["payouts", params],
    queryFn: async () => {
      const response = await apiInstance.get<PaginatedResponse<Payment>>(
        "/payments",
        params,
      );

      return response.data;
    },
  });
};

export const useApplications = ({
  pageSize,
  currentPage,
  search,
}: TableApiCallParams) => {
  return useQuery<PaginatedResponse<Payment>>({
    queryKey: ["applications"],
    queryFn: async () => {
      const { data } = await apiInstance.get<PaginatedResponse<Payment>>(
        "/payments",
        {
          pageSize,
          currentPage,
          search,
        },
      );
      return data;
    },
  });
};

export const useRequestPayment = () => {
  const { toast } = useToast();

  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["requestPayment"],
    mutationFn: async (data: RequestPaymentRequest) => {
      const response = await apiInstance.post<RequestPaymentRequest>(
        "/payments/request",
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: "Успех!",
        variant: "default",
        description: "Вы запросили платёж.",
      });
      queryClient.refetchQueries({ queryKey: ["balance"] });
    },
    onError: (_, data) => {
      if (data.amount < 20)
        toast({
          title: "Ошибка",
          variant: "destructive",
          description: "Минимальная сумма выплаты 20$",
        });
      else
        toast({
          title: "Ошибка",
          variant: "destructive",
          description: "Не удалось запросить платёж.",
        });
    },
  });
};
